import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Page } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  addDeskCandidate,
  claimDeskCandidate,
  createDesk,
  deskEventsQueryOptions,
  deskQueueQueryOptions,
  desksQueryOptions,
  feedQueryOptions,
  pinDeskCandidate,
  promoteDeskCandidate,
  rejectDeskCandidate,
  setDeskFrozen,
  syncDeskCandidates,
  type Desk,
  type DeskCandidate,
} from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Curation Desk — several people working one feed's queue during a live event.
 *
 * DESIGNED FOR SPEED, NOT FOR BROWSING. On election night a curator is deciding on posts
 * every few seconds while reading something else, so the whole queue is keyboard-driven:
 * j/k to move, p to promote, x to reject, c to claim. Buttons exist for discoverability
 * and for anyone who would rather point, but the keys are the real interface.
 *
 * The UI never decides who may do what — it only hides what the server would refuse anyway.
 * Permission lives in `authorize_desk`, and a desk role is a VIEW OF THE FEED'S COLLABORATOR
 * CAPABILITIES rather than a roster of its own: manage_collaborators reads as owner,
 * add_posts_* as editor, suggest_posts as reviewer, and no capability as no standing at all.
 * So `your_role` below changes when somebody's capabilities change, and removing them from
 * the feed closes the desk to them with it — there is no second list to remember.
 *
 * Two things are shown loudly rather than tucked away:
 *   - FROZEN. The kill switch stops every promotion at once. If it is on, that is the most
 *     important fact on the page and nothing below it will work.
 *   - Someone else's claim. Two curators reaching for the same post at 9pm is the failure
 *     this queue exists to prevent, so a held item is visibly unavailable, not silently
 *     refused after you act.
 */
export const Route = createFileRoute("/app/_sidebar-layout/feed/$feedId/desk")({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    await Promise.all([
      queryClient.ensureQueryData(feedQueryOptions(parseInt(feedId))),
      queryClient.ensureQueryData(desksQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Curation Desk | Graze" }] }),
});

// A reason code rather than free text: it is how we learn what the candidate feed's rules
// are getting wrong. `as const` so the first entry types as a string, not string | undefined.
const REJECT_REASONS = [
  "Off topic",
  "Already covered",
  "Unverified claim",
  "Low quality source",
  "Duplicate",
] as const;
const DEFAULT_REJECT_REASON = REJECT_REASONS[0];

function postUrlFor(atUri: string) {
  // at://did/app.bsky.feed.post/rkey -> a link a curator can actually open
  const parts = atUri.split("/");
  return parts.length >= 5
    ? `https://bsky.app/profile/${parts[2]}/post/${parts[4]}`
    : atUri;
}

function CreateDeskPanel({ feedId }: { feedId: number }) {
  const [name, setName] = useState("");
  const [candidateFeedId, setCandidateFeedId] = useState("");
  const create = useMutation({
    mutationFn: () =>
      createDesk({
        display_name: name || "Curation desk",
        algorithm_id: feedId,
        candidate_algorithm_id: candidateFeedId
          ? parseInt(candidateFeedId)
          : null,
      }),
  });

  return (
    <div className="max-w-xl space-y-4 rounded-lg border p-6">
      <div>
        <h2 className="text-lg font-semibold">No desk on this feed yet</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          A desk lets people who do not own this feed suggest and promote posts into
          it, with an audit trail and a freeze switch. You stay the owner.
        </p>
      </div>
      <label htmlFor="desk-name" className="block space-y-1">
        <span className="text-sm font-medium">Desk name</span>
        <Input
          id="desk-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Election night desk"
        />
      </label>
      <label htmlFor="desk-candidate-feed" className="block space-y-1">
        <span className="text-sm font-medium">
          Candidate feed ID <span className="font-normal text-gray-500">(optional)</span>
        </span>
        <Input
          id="desk-candidate-feed"
          value={candidateFeedId}
          onChange={(e) => setCandidateFeedId(e.target.value)}
          placeholder="e.g. 36495"
          inputMode="numeric"
        />
        <span className="block text-xs text-gray-500">
          A second feed of your own with looser rules. Its matches become this desk's
          queue, so the published feed can stay tight. Leave blank to add posts by hand.
        </span>
      </label>
      <Button onClick={() => create.mutate()} disabled={create.isPending}>
        {create.isPending ? "Creating…" : "Create desk"}
      </Button>
      {create.isError && (
        <p className="text-sm text-red-600">
          Could not create the desk. You must own this feed to open one on it.
        </p>
      )}
    </div>
  );
}

function CandidateRow({
  candidate,
  selected,
  busy,
  canPromote,
  onSelect,
}: {
  candidate: DeskCandidate;
  selected: boolean;
  busy: boolean;
  canPromote: boolean;
  onSelect: () => void;
}) {
  return (
    <li
      role="option"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex cursor-pointer items-center gap-3 border-l-4 px-4 py-3",
        selected
          ? "border-l-blue-500 bg-blue-50 dark:bg-blue-950"
          : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-900",
        busy && "opacity-50",
      )}
    >
      <div className="min-w-0 flex-1">
        <a
          href={postUrlFor(candidate.post_uri)}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="block truncate text-sm underline-offset-2 hover:underline"
        >
          {candidate.post_uri}
        </a>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
          <span>{candidate.source.replace("_", " ")}</span>
          {candidate.state === "escalated" && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              needs a second pair of eyes
            </span>
          )}
          {candidate.claim_held && (
            <span className="rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-800">
              someone else is on this
            </span>
          )}
        </div>
      </div>
      {!canPromote && (
        <span className="shrink-0 text-xs text-gray-500">suggest only</span>
      )}
    </li>
  );
}

function DeskView({ desk }: { desk: Desk }) {
  const { data: queue } = useSuspenseQuery(deskQueueQueryOptions(desk.id));
  const { data: events } = useQuery(deskEventsQueryOptions(desk.id));
  const [cursor, setCursor] = useState(0);
  const [pasted, setPasted] = useState("");
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const candidates = queue.candidates;
  const canPromote = queue.your_role === "editor" || queue.your_role === "owner";
  const current = candidates[cursor];

  const act = useMutation({
    mutationFn: async (action: "claim" | "promote" | "reject" | "pin") => {
      if (!current) return;
      if (action === "claim") return claimDeskCandidate(desk.id, current.id);
      if (action === "promote") return promoteDeskCandidate(desk.id, current.id);
      if (action === "pin") return pinDeskCandidate(desk.id, current.id, 120);
      return rejectDeskCandidate(desk.id, current.id, DEFAULT_REJECT_REASON);
    },
    onSuccess: () => {
      setError(null);
      // Stay at the same index: the row we just acted on leaves the open queue, so the
      // next one slides into place. Advancing as well would skip a post.
      setCursor((c) => Math.min(c, Math.max(0, candidates.length - 2)));
    },
    onError: (e: unknown) => {
      // The server is the authority on every refusal - frozen desk, held claim, role,
      // hourly cap - so show what it said rather than guessing in the client.
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        "That did not go through.";
      setError(detail);
    },
  });

  const sync = useMutation({ mutationFn: () => syncDeskCandidates(desk.id) });
  const add = useMutation({
    mutationFn: () => addDeskCandidate(desk.id, pasted),
    onSuccess: () => {
      setPasted("");
      setError(null);
    },
    onError: () => setError("That link could not be resolved to a post."),
  });
  const freeze = useMutation({
    mutationFn: () => setDeskFrozen(desk.id, !desk.is_frozen),
  });

  const move = useCallback(
    (delta: number) =>
      setCursor((c) => Math.max(0, Math.min(candidates.length - 1, c + delta))),
    [candidates.length],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Never steal keys from someone typing a URL into the paste box.
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const keys: Record<string, () => void> = {
        j: () => move(1),
        k: () => move(-1),
        ArrowDown: () => move(1),
        ArrowUp: () => move(-1),
        c: () => act.mutate("claim"),
        p: () => canPromote && act.mutate("promote"),
        x: () => act.mutate("reject"),
      };
      const handler = keys[e.key];
      if (handler) {
        e.preventDefault();
        handler();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, act, canPromote]);

  useEffect(() => {
    listRef.current?.children[cursor]?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const recent = useMemo(() => (events?.events ?? []).slice(0, 8), [events]);

  return (
    <div className="space-y-6">
      {desk.is_frozen && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
          <p className="font-semibold text-red-900 dark:text-red-100">
            This desk is frozen
          </p>
          <p className="text-sm text-red-800 dark:text-red-200">
            Nothing can be promoted or pinned until it is unfrozen. Everything else on
            this page still works.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && pasted && add.mutate()}
          placeholder="Paste a post link to add it to the queue"
          className="max-w-md"
        />
        <Button
          variant="outline"
          onClick={() => add.mutate()}
          disabled={!pasted || add.isPending}
        >
          Add
        </Button>
        {desk.candidate_algorithm_id && (
          <Button
            variant="outline"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            {sync.isPending ? "Pulling…" : "Pull from candidate feed"}
          </Button>
        )}
        {queue.your_role === "owner" && (
          <Button
            variant={desk.is_frozen ? "default" : "outline"}
            onClick={() => freeze.mutate()}
            disabled={freeze.isPending}
          >
            {desk.is_frozen ? "Unfreeze desk" : "Freeze desk"}
          </Button>
        )}
      </div>

      {error && (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {error}
        </p>
      )}

      <div className="rounded-lg border">
        <div className="flex items-center justify-between border-b px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
          <span>
            {candidates.length} waiting
            {sync.data ? ` · pulled ${sync.data.added}` : ""}
          </span>
          <span className="hidden sm:inline">
            <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>c</kbd> claim ·{" "}
            {canPromote && (
              <>
                <kbd>p</kbd> promote ·{" "}
              </>
            )}
            <kbd>x</kbd> reject
          </span>
        </div>
        {candidates.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">
            Nothing waiting. Paste a link above
            {desk.candidate_algorithm_id ? ", or pull from the candidate feed." : "."}
          </p>
        ) : (
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Candidates waiting for a decision"
            className="max-h-[32rem] overflow-y-auto"
          >
            {candidates.map((c, i) => (
              <CandidateRow
                key={c.id}
                candidate={c}
                selected={i === cursor}
                busy={act.isPending && i === cursor}
                canPromote={canPromote}
                onSelect={() => setCursor(i)}
              />
            ))}
          </ul>
        )}
      </div>

      {current && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => act.mutate("claim")}>
            Claim
          </Button>
          {canPromote && (
            <>
              <Button onClick={() => act.mutate("promote")}>Promote to feed</Button>
              <Button variant="outline" onClick={() => act.mutate("pin")}>
                Pin for 2 hours
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => act.mutate("reject")}>
            Reject
          </Button>
        </div>
      )}

      {recent.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">Recent activity</h3>
          <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
            {recent.map((e) => (
              <li key={e.id}>
                <span className="font-medium">{e.action}</span>
                {e.at ? ` · ${new Date(e.at).toLocaleTimeString()}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RouteComponent() {
  const { feedId } = Route.useParams();
  const id = parseInt(feedId);
  const { data: feed } = useSuspenseQuery(feedQueryOptions(id));
  const { data: desks } = useSuspenseQuery(desksQueryOptions());
  const desk = desks.desks.find((d) => d.algorithm_id === id);

  return (
    <Page>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Curation desk</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">{feed.display_name}</p>
      </div>
      {desk ? <DeskView desk={desk} /> : <CreateDeskPanel feedId={id} />}
    </Page>
  );
}
